import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Coffee, Plus, Trash2, Pencil, X, Snowflake, Flame, Copy, Search } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "./ConfirmDialog";
import { CardSkeleton } from "./Skeletons";

const HOT_FIELDS = ["gram", "ratio", "temp", "pour", "time"];
const ICED_FIELDS = ["gram", "ice", "temp", "pour", "time"];

const LABELS = {
  gram: { en: "Gram", ar: "جرام" },
  ratio: { en: "Ratio", ar: "النسبة" },
  ice: { en: "Ice", ar: "الثلج" },
  temp: { en: "Temp", ar: "الحرارة" },
  pour: { en: "Pour", ar: "السكب" },
  time: { en: "Time", ar: "الزمن" },
};

export default function EspressoTab() {
  const { isRTL } = useI18n();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");

  const { data: recipes = [], isLoading } = useQuery({
    queryKey: ["mycafe-espresso"], queryFn: endpoints.myCafeEspresso,
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return recipes;
    return recipes.filter((r) =>
      [r.name, r.name_ar, r.notes].filter(Boolean).some((s) => s.toLowerCase().includes(q))
    );
  }, [recipes, query]);

  const empty = { name: "", name_ar: "", hot: {}, iced: {}, notes: "" };

  const startNew = () => setEditing({ id: null, ...empty });
  const startEdit = (r) => setEditing({ ...r, hot: r.hot || {}, iced: r.iced || {} });
  const cancel = () => setEditing(null);

  const save = useMutation({
    mutationFn: async () => {
      const payload = { name: editing.name, name_ar: editing.name_ar, hot: editing.hot, iced: editing.iced, notes: editing.notes };
      if (!payload.name?.trim()) throw new Error(isRTL ? "اسم المشروب مطلوب" : "Drink name required");
      return editing.id ? endpoints.myCafeUpdateEspresso(editing.id, payload) : endpoints.myCafeCreateEspresso(payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mycafe-espresso"] }); toast.success(isRTL ? "تم الحفظ" : "Saved"); cancel(); },
    onError: (e) => toast.error(e.message || "Error"),
  });

  const del = useMutation({
    mutationFn: (id) => endpoints.myCafeDeleteEspresso(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mycafe-espresso"] }); toast.success(isRTL ? "تم الحذف" : "Deleted"); },
  });

  const duplicate = useMutation({
    mutationFn: async (r) => endpoints.myCafeCreateEspresso({
      name: `${r.name} (${isRTL ? "نسخة" : "copy"})`,
      name_ar: r.name_ar ? `${r.name_ar} (نسخة)` : undefined,
      hot: r.hot, iced: r.iced, notes: r.notes,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mycafe-espresso"] }); toast.success(isRTL ? "تم النسخ" : "Duplicated"); },
  });

  const handleDelete = async (r) => {
    const ok = await confirm({
      title: isRTL ? `حذف "${r.name}"؟` : `Delete "${r.name}"?`,
      destructive: true,
      confirmLabel: isRTL ? "نعم، احذف" : "Yes, delete",
    });
    if (ok) del.mutate(r.id);
  };

  return (
    <div className="space-y-6" data-testid="mycafe-espresso">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-[#5C544F]">
          {isRTL ? "وصفات الإسبريسو الخاصة بمقهاك. كل وصفة تحتوي نسخة حارة وأخرى مثلجة." : "Your private espresso recipes — each with a hot and iced spec card."}
        </p>
        {!editing && (
          <Button onClick={startNew} className="bg-[#2A2421] text-[#F9F6F0]" data-testid="espresso-add-btn">
            <Plus className="w-4 h-4 me-1.5" />
            {isRTL ? "وصفة جديدة" : "New recipe"}
          </Button>
        )}
      </div>

      {editing && (
        <div className="soft-card rounded-2xl p-6 space-y-5" data-testid="espresso-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{isRTL ? "اسم المشروب (EN)" : "Drink name (EN)"} *</Label>
              <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Cortado" className="mt-1.5 h-11" data-testid="espresso-name-input" />
            </div>
            <div>
              <Label>{isRTL ? "الاسم بالعربي" : "Name (AR)"}</Label>
              <Input value={editing.name_ar || ""} onChange={(e) => setEditing({ ...editing, name_ar: e.target.value })} placeholder="مثال: كورتادو" className="mt-1.5 h-11" data-testid="espresso-name-ar-input" />
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <SideCard
              title={isRTL ? "النسخة الحارة" : "HOT version"}
              icon={Flame}
              accent="#C86240"
              fields={HOT_FIELDS}
              data={editing.hot}
              onChange={(k, v) => setEditing({ ...editing, hot: { ...editing.hot, [k]: v } })}
              testId="hot"
            />
            <SideCard
              title={isRTL ? "النسخة المثلجة" : "ICED version"}
              icon={Snowflake}
              accent="#3D6F86"
              fields={ICED_FIELDS}
              data={editing.iced}
              onChange={(k, v) => setEditing({ ...editing, iced: { ...editing.iced, [k]: v } })}
              testId="iced"
            />
          </div>

          <div>
            <Label>{isRTL ? "ملاحظات" : "Notes"}</Label>
            <Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} className="mt-1.5" data-testid="espresso-notes" />
          </div>

          <div className="flex gap-2">
            <Button onClick={() => save.mutate()} className="bg-[#C86240] text-white" data-testid="espresso-save-btn">{isRTL ? "حفظ" : "Save"}</Button>
            <Button onClick={cancel} variant="outline"><X className="w-4 h-4 me-1.5" />{isRTL ? "إلغاء" : "Cancel"}</Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <CardSkeleton rows={3} />
      ) : recipes.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#2A2421]/15 p-12 text-center bg-[#F9F6F0]/50">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#C86240]/10 flex items-center justify-center mb-4">
            <Coffee className="w-7 h-7 text-[#C86240]" />
          </div>
          <div className="text-[#2A2421] font-medium text-lg">{isRTL ? "لا توجد وصفات إسبريسو بعد" : "No espresso recipes yet"}</div>
          <div className="text-sm text-[#5C544F] mt-1 max-w-md mx-auto">
            {isRTL ? "ابدأ بأول وصفة إسبريسو خاصة بمقهاك." : "Start by adding your first private espresso recipe."}
          </div>
        </div>
      ) : (
        <>
          {recipes.length > 3 && (
            <div className="relative max-w-sm">
              <Search className={`absolute ${isRTL ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C544F]`} />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={isRTL ? "ابحث في الوصفات..." : "Search recipes..."}
                className={`${isRTL ? "pr-9" : "pl-9"} h-10`}
                data-testid="espresso-search"
              />
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((r) => (
              <RecipeCard
                key={r.id}
                r={r}
                isRTL={isRTL}
                onEdit={() => startEdit(r)}
                onDuplicate={() => duplicate.mutate(r)}
                onDelete={() => handleDelete(r)}
              />
            ))}
          </div>
        </>
      )}

      {dialog}
    </div>
  );
}

function SideCard({ title, icon: Icon, accent, fields, data, onChange, testId }) {
  const { isRTL } = useI18n();
  return (
    <div className="rounded-2xl border p-4" style={{ borderColor: `${accent}33` }}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] mb-3" style={{ color: accent }}>
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {fields.map((f) => (
          <div key={f}>
            <Label className="text-xs">{LABELS[f][isRTL ? "ar" : "en"]}</Label>
            <Input
              value={data?.[f] || ""}
              onChange={(e) => onChange(f, e.target.value)}
              className="mt-1 h-10"
              placeholder="—"
              data-testid={`espresso-${testId}-${f}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function RecipeCard({ r, isRTL, onEdit, onDuplicate, onDelete }) {
  const lang = isRTL ? "ar" : "en";
  return (
    <div className="soft-card rounded-2xl p-5 hover:shadow-md transition-shadow" data-testid={`espresso-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`text-lg ${isRTL ? "font-serif-ar" : "font-serif-en"} text-[#2A2421] truncate`}>{isRTL && r.name_ar ? r.name_ar : r.name}</div>
          {isRTL && r.name_ar && <div className="text-xs text-[#5C544F]">{r.name}</div>}
        </div>
        <div className="flex gap-1 shrink-0">
          <button onClick={onEdit} className="p-1.5 hover:bg-[#2A2421]/5 rounded-full" data-testid={`espresso-edit-${r.id}`} title={isRTL ? "تعديل" : "Edit"}>
            <Pencil className="w-3.5 h-3.5 text-[#5C544F]" />
          </button>
          <button onClick={onDuplicate} className="p-1.5 hover:bg-[#2A2421]/5 rounded-full" data-testid={`espresso-dup-${r.id}`} title={isRTL ? "تكرار" : "Duplicate"}>
            <Copy className="w-3.5 h-3.5 text-[#5C544F]" />
          </button>
          <button onClick={onDelete} className="p-1.5 hover:bg-red-50 rounded-full" data-testid={`espresso-del-${r.id}`} title={isRTL ? "حذف" : "Delete"}>
            <Trash2 className="w-3.5 h-3.5 text-red-500" />
          </button>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        <MiniSpec title={isRTL ? "حار" : "Hot"} fields={HOT_FIELDS} data={r.hot} accent="#C86240" lang={lang} />
        <MiniSpec title={isRTL ? "مثلج" : "Iced"} fields={ICED_FIELDS} data={r.iced} accent="#3D6F86" lang={lang} />
      </div>
      {r.notes && <p className="mt-3 text-xs text-[#5C544F] italic">{r.notes}</p>}
    </div>
  );
}

function MiniSpec({ title, fields, data, accent, lang }) {
  if (!data || Object.values(data).every((v) => !v)) return null;
  return (
    <div className="rounded-lg p-2.5 text-xs" style={{ background: `${accent}11`, color: "#2A2421" }}>
      <div className="font-semibold mb-1" style={{ color: accent }}>{title}</div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        {fields.map((f) => data?.[f] ? (
          <span key={f}><b>{LABELS[f][lang]}:</b> {data[f]}</span>
        ) : null)}
      </div>
    </div>
  );
}
